import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Heart, 
  MapPin,
  Phone,
  Star,
  Target,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { hospitalInfo } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { useCMSPage } from '../hooks/useCMSPage';
import InnerPageLayout from '../components/InnerPageLayout';

const OurMission = () => {
  const location = useLocation();
  const { pageData, loading: cmsLoading } = useCMSPage(location.pathname);
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';

  // Default hardcoded content (fallback if no CMS content)
  const defaultServiceAreas = [
    "South Riding", "Chantilly", "Aldie", "Ashburn", "Herndon", "Centreville", "Fairfax"
  ];

  const defaultMissionPoints = [
    {
      title: "Quality Pet Care for All",
      description: "We believe quality pet care, food, medicines, and pet supplies should be available to all caring and responsible pet owners",
      icon: Heart
    },
    {
      title: "State-of-the-Art Facility",
      description: "Provide high-quality treatment options in our modern facility equipped with the latest veterinary technologies",
      icon: Star
    },
    {
      title: "Comprehensive Services",
      description: "Our licensed veterinarians provide vaccinations, parasite control, and comprehensive care to keep your pets healthy",
      icon: CheckCircle
    }
  ];

  // Use CMS data if available, otherwise use defaults
  const title = pageData?.title || "Our Mission";
  const badgeText = pageData?.badge_text || "Excellent Modern Care for the Modern Pet";
  const description = pageData?.description || "Pets & Vets Animal Hospital delivers the very best veterinary care to your pets. Our team of licensed veterinarians provides vaccinations, parasite control, and comprehensive care.";

  // Hero content for the left side
  const heroContent = (
    <div className="text-center lg:text-left">
      <h1 className="text-xl font-bold text-gray-900 mb-4">
        {title}
      </h1>
      <div className="mb-4">
        <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold text-white" style={{ backgroundColor: primaryColor }}>
          {badgeText}
        </span>
      </div>
      <p className="text-base text-gray-600 leading-relaxed">
        {description}
      </p>
    </div>
  );

  // Render CMS main content if available
  const renderMainContent = () => {
    if (pageData?.main_content) {
      // Split content by double newlines to create paragraphs
      const paragraphs = pageData.main_content.split('\n\n').filter(p => p.trim());
      return (
        <section className="bg-[#f8f9fa] py-6">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-base text-gray-600 leading-relaxed text-center">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>
      );
    }
    return null;
  };

  // Check if we have CMS main content
  const hasCMSContent = pageData?.main_content && pageData.main_content.trim().length > 0;

  return (
    <InnerPageLayout heroContent={heroContent} customImage={pageData?.hero_image}>
    <div className="bg-gray-50">
      {/* Render CMS main content if available, otherwise show default content */}
      {hasCMSContent ? (
        renderMainContent()
      ) : (
        <>
          {/* Default Mission Statement */}
          <section className="bg-[#f8f9fa] py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-6">
                <p className="text-base text-gray-600 leading-relaxed text-center">
                  We believe quality pet care, food, medicines, and pet supplies should be available to all caring and 
                  responsible pet owners. It is our mission to provide high-quality treatment options for your pets in our 
                  state-of-the-art facility, which is equipped with the latest veterinary technologies.
                </p>
                
                <div className="p-8 rounded-xl border-l-4 bg-white" style={{ borderColor: primaryColor }}>
                  <div className="flex items-center justify-center mb-4">
                    <Heart className="h-6 w-6 mr-3" style={{ color: primaryColor }} />
                    <h3 className="text-xl font-semibold text-gray-900">Our Promise</h3>
                  </div>
                  <p className="font-medium italic text-gray-800 text-center" style={{ fontSize: '1rem' }}>
                    "We strive to provide excellent modern care for the modern pet, because every pet deserves the best possible care."
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Default Mission Points */}
          <section className="bg-white py-6">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xl font-bold text-gray-900 mb-8 text-center">
                How We Fulfill Our Mission
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {defaultMissionPoints.map((point, index) => (
                  <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-md text-center">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                      <point.icon className="h-6 w-6" style={{ color: primaryColor }} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{point.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Default Service Areas */}
          <section className="bg-[#f8f9fa] py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Proudly Serving Northern Virginia
              </h2>
              <p className="text-gray-600 mb-6">
                Delivering exceptional veterinary care across these communities:
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {defaultServiceAreas.map((area, index) => (
                  <span key={index} className="px-4 py-2 rounded-full text-white font-medium" style={{ backgroundColor: primaryColor }}>
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Contact CTA - Always show */}
      <section style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)`, paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-white mb-4" style={{ fontSize: '1rem' }}>
            Experience Our Mission in Action
          </h2>
          <p className="mb-8" style={{ color: 'white', fontSize: '1rem' }}>
            Contact us today to schedule an appointment and see how we put our mission into practice
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${currentBusinessInfo.phone}`}
              className="inline-flex items-center justify-center bg-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              style={{ color: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now: {currentBusinessInfo.phone}
            </a>
            <Link
              to="/reach-us"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';  
                e.target.style.color = 'white';
              }}
            >
              Schedule Visit
              <MapPin className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </InnerPageLayout>
  );
};

export default OurMission;